import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage.js";
import DashboardPage from "./pages/DashboardPage.js";
import ClientesPage from "./pages/ClientesPage.js";
import PlanosPage from "./pages/PlanosPage.js";
import FiliaisPage from "./pages/FiliaisPage.js";
import UsuariosPage from "./pages/UsuariosPage.js";
import FinanceiroPage from "./pages/FinanceiroPage.js";
import BoletosPage from "./pages/BoletosPage.js";
import IXCIntegracaoPage from "./pages/IXCIntegracaoPage.js";
import OrdensServicoPage from "./pages/OrdensServicoPage.js";
import AtendimentoPublicoPage from "./pages/AtendimentoPublicoPage.js";
import AtendimentosPage from "./pages/AtendimentosPage.js";
import ChatInternoPage from "./pages/ChatInternoPage.js";
import MensagensPage from "./pages/MensagensPage.js";
import WhatsAppConfigPage from "./pages/WhatsAppConfigPage.js";
import ConfigMensagensPage from "./pages/ConfigMensagensPage.js";
import LixeiraPage from "./pages/LixeiraPage.js";
import RastreioPage from "./pages/RastreioPage.js";
import FrotaPage from "./pages/FrotaPage.js";
import TransmissaoPage from "./pages/TransmissaoPage.js";
import ConfiguracaoSomPage from "./pages/ConfiguracaoSomPage.js";
import IAPage from "./pages/IAPage.js";
import Layout from "./components/Layout.js";
import NotificacaoSom from "./components/NotificacaoSom.js";

// Detecta automaticamente a URL do backend baseado no domínio atual
function getBackendUrl() {
  // Se tiver variável de ambiente definida, usa ela
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Senão, usa o mesmo domínio da página atual (para produção)
  return window.location.origin;
}

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

export const handleApiError = (error) => {
  if (error.response && error.response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }
  throw error;
};

// Função para atualizar permissões do usuário em tempo real
export const refreshUserPermissions = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const response = await fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.status === 401) {
      // Token inválido ou usuário desativado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return null;
    }
    
    if (response.ok) {
      const userData = await response.json();
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Verificar se as permissões mudaram
      const permissoesAtual = JSON.stringify(currentUser.permissoes || {});
      const permissoesNova = JSON.stringify(userData.permissoes || {});
      
      if (permissoesAtual !== permissoesNova || currentUser.is_super_admin !== userData.is_super_admin) {
        // Permissões mudaram - atualizar localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Forçar atualização da página para aplicar novas permissões
        window.location.reload();
      }
      
      return userData;
    }
  } catch (error) {
    console.error('Erro ao atualizar permissões:', error);
  }
  
  return null;
};

// Iniciar verificação periódica de permissões (a cada 30 segundos)
let permissionCheckInterval = null;
export const startPermissionCheck = () => {
  if (permissionCheckInterval) return;
  
  permissionCheckInterval = setInterval(() => {
    if (isAuthenticated()) {
      refreshUserPermissions();
    }
  }, 30000); // 30 segundos
};

export const stopPermissionCheck = () => {
  if (permissionCheckInterval) {
    clearInterval(permissionCheckInterval);
    permissionCheckInterval = null;
  }
};

// Função para verificar permissão
const hasPermission = (permissao) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Super admin tem acesso a tudo
  if (user.is_super_admin) return true;
  
  // Admin Master também tem acesso a tudo
  const role = (user.role || '').toLowerCase();
  const tipo = (user.tipo || '').toLowerCase();
  const cargo = (user.cargo || '').toLowerCase();
  const name = (user.name || '').toLowerCase();
  
  if (role === 'admin' || role === 'admin master' || role === 'super admin') return true;
  if (tipo === 'admin' || tipo === 'admin master' || tipo === 'super admin') return true;
  if (cargo === 'admin' || cargo === 'admin master' || cargo === 'super admin') return true;
  if (name.includes('admin')) return true;
  
  // Se não tem permissões definidas, bloqueia tudo exceto dashboard
  if (!user.permissoes) {
    return permissao === 'dashboard';
  }
  
  // Para ordens_servico, aceitar qualquer permissão relacionada a OS
  if (permissao === 'ordens_servico') {
    return user.permissoes.ordens_servico === true || 
           user.permissoes.ordens_servico_tecnico === true ||
           user.permissoes.ordens_servico_criar === true ||
           user.permissoes.ordens_servico_editar === true;
  }
  
  // Verifica permissão específica
  return user.permissoes[permissao] === true;
};

// Encontra a primeira página que o usuário tem acesso
const findFirstAllowedPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const permissoes = user.permissoes || {};
  
  // Ordem de prioridade para redirecionamento
  const paginas = [
    { permissao: 'dashboard', path: '/dashboard' },
    { permissao: 'ordens_servico', path: '/ordens-servico' },
    { permissao: 'atendimentos', path: '/atendimentos' },
    { permissao: 'clientes', path: '/clientes' },
    { permissao: 'chat_interno', path: '/chat' },
    { permissao: 'mensagens', path: '/mensagens' }
  ];
  
  for (const pagina of paginas) {
    if (hasPermission(pagina.permissao)) {
      return pagina.path;
    }
  }
  
  return '/dashboard'; // fallback
};

const ProtectedRoute = ({ children, hideSidebar, permissao }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  
  // Se tem permissão definida, verifica
  if (permissao && !hasPermission(permissao)) {
    // Redireciona para a primeira página permitida
    const allowedPage = findFirstAllowedPage();
    return <Navigate to={allowedPage} replace />;
  }
  
  return <Layout hideSidebar={hideSidebar}>{children}</Layout>;
};

function App() {
  return (
    <div className="min-h-screen bg-slate-950">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/atendimento" element={<AtendimentoPublicoPage />} />
          <Route path="/" element={<ProtectedRoute permissao="dashboard"><DashboardPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute permissao="dashboard"><DashboardPage /></ProtectedRoute>} />
          <Route path="/ordens-servico" element={<ProtectedRoute permissao="ordens_servico"><OrdensServicoPage /></ProtectedRoute>} />
          <Route path="/atendimentos" element={<ProtectedRoute permissao="atendimentos" hideSidebar={true}><AtendimentosPage /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute permissao="chat_interno"><ChatInternoPage /></ProtectedRoute>} />
          <Route path="/mensagens" element={<ProtectedRoute permissao="mensagens"><MensagensPage /></ProtectedRoute>} />
          <Route path="/clientes" element={<ProtectedRoute permissao="clientes"><ClientesPage /></ProtectedRoute>} />
          <Route path="/planos" element={<ProtectedRoute permissao="planos"><PlanosPage /></ProtectedRoute>} />
          <Route path="/filiais" element={<ProtectedRoute permissao="filiais"><FiliaisPage /></ProtectedRoute>} />
          <Route path="/boletos" element={<ProtectedRoute permissao="boletos"><BoletosPage /></ProtectedRoute>} />
          <Route path="/financeiro" element={<ProtectedRoute permissao="financeiro"><FinanceiroPage /></ProtectedRoute>} />
          <Route path="/ixc" element={<ProtectedRoute permissao="ixc"><IXCIntegracaoPage /></ProtectedRoute>} />
          <Route path="/whatsapp" element={<ProtectedRoute permissao="whatsapp"><WhatsAppConfigPage /></ProtectedRoute>} />
          <Route path="/config-mensagens" element={<ProtectedRoute permissao="whatsapp"><ConfigMensagensPage /></ProtectedRoute>} />
          <Route path="/config-som" element={<ProtectedRoute permissao="usuarios"><ConfiguracaoSomPage /></ProtectedRoute>} />
          <Route path="/ia" element={<ProtectedRoute permissao="usuarios"><IAPage /></ProtectedRoute>} />
          <Route path="/usuarios" element={<ProtectedRoute permissao="usuarios"><UsuariosPage /></ProtectedRoute>} />
          <Route path="/rastreio" element={<ProtectedRoute permissao="ordens_servico"><RastreioPage /></ProtectedRoute>} />
          <Route path="/frota" element={<ProtectedRoute permissao="ordens_servico"><FrotaPage /></ProtectedRoute>} />
          <Route path="/transmissao" element={<ProtectedRoute permissao="atendimentos"><TransmissaoPage /></ProtectedRoute>} />
          <Route path="/lixeira" element={<ProtectedRoute permissao="usuarios"><LixeiraPage /></ProtectedRoute>} />
        </Routes>
        {/* Componente global de notificação sonora */}
        <NotificacaoSom />
      </BrowserRouter>
      <Toaster position="top-center" richColors toastOptions={{ style: { marginTop: '60px' } }} />
    </div>
  );
}

export default App;
